/**
 * Cognito Authentication Context
 *
 * Provides authentication state and methods for AWS Cognito
 * Replaces Auth0 authentication with Cognito-based flow
 */

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ApolloProvider, gql } from '@apollo/client';
import {
  useAuthCheckUserMutation,
  useAuthLoginOAuthMutation,
  useAuthLoginPasswordMutation,
  useAuthRefreshTokenMutation,
  useAuthSendOtpMutation,
  useAuthSetPasswordMutation,
  useAuthVerifyOtpMutation,
} from 'lib/graphql';
import { createUnauthApolloClient } from 'lib/graphql/unauth-client';
import { isPlaywrightTestMode } from 'lib/test-mode';

interface CognitoUser {
  id: string;
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  tenantId: string;
  role: string;
  authMethod: 'password' | 'otp';
}

interface CognitoAuthState {
  user: CognitoUser | null;
  accessToken: string | null;
  idToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  impersonateUserId: string | null;
}

interface CognitoAuthContextValue extends CognitoAuthState {
  checkUser: (
    identifier: string,
    redirectPath?: string,
  ) => Promise<{
    exists: boolean;
    authMethod?: 'password' | 'otp' | 'google-sso';
    requiresPasswordReset?: boolean;
    redirectUrl?: string;
    otpSent?: boolean;
    session?: string;
    message?: string;
    resetCodeSent?: boolean;
    resetDestination?: string;
  }>;
  loginWithPassword: (identifier: string, password: string) => Promise<void>;
  loginWithOTP: (
    identifier: string,
    otp: string,
    session?: string,
  ) => Promise<{ requiresProfileCompletion: boolean }>;
  loginWithOAuthCode: (code: string) => Promise<void>;
  sendOTP: (
    identifier: string,
  ) => Promise<{ success: boolean; session?: string }>;
  forgotPassword: (
    identifier: string,
  ) => Promise<{ success: boolean; destination?: string }>;
  confirmForgotPassword: (
    identifier: string,
    code: string,
    newPassword: string,
  ) => Promise<void>;
  setNewPassword: (
    identifier: string,
    newPassword: string,
    temporaryPassword?: string,
  ) => Promise<void>;
  logout: (options?: { preserveRedirect?: boolean }) => Promise<void>;
  refreshAccessToken: () => Promise<string>;
  checkTokenExpiry: (idToken: string | null) => boolean;
  setImpersonateUserId: (userId: string | null) => void;
  clearImpersonation: () => void;
}

export const CognitoAuthContext = createContext<CognitoAuthContextValue | null>(
  null,
);

interface CognitoAuthProviderProps {
  children: ReactNode;
}

const TOKEN_STORAGE_KEY = 'cognito_auth';

// Create a singleton unauth Apollo Client for auth operations
const unauthApolloClient = createUnauthApolloClient();

/**
 * Inner provider component that uses Apollo hooks
 * Must be inside ApolloProvider context
 * @param props - Component props
 * @param props.children - Child components
 * @returns Provider component with auth context
 */
function CognitoAuthProviderInner({ children }: CognitoAuthProviderProps) {
  const [state, setState] = useState<CognitoAuthState>({
    user: null,
    accessToken: null,
    idToken: null,
    refreshToken: null,
    isAuthenticated: false,
    isLoading: true,
    impersonateUserId: null,
  });

  const navigate = useNavigate();
  const location = useLocation();

  // Initialize GraphQL mutation hooks
  const [authCheckUserMutation] = useAuthCheckUserMutation();
  const [authLoginPasswordMutation] = useAuthLoginPasswordMutation();
  const [authLoginOAuthMutation] = useAuthLoginOAuthMutation();
  const [authSendOTPMutation] = useAuthSendOtpMutation();
  const [authVerifyOTPMutation] = useAuthVerifyOtpMutation();
  const [authSetPasswordMutation] = useAuthSetPasswordMutation();
  const [authRefreshTokenMutation] = useAuthRefreshTokenMutation();

  /**
   * Parse user info from ID token
   * @param idToken - The JWT ID token to parse
   * @returns Parsed user object with claims
   */
  const parseUserFromToken = useCallback((idToken: string): CognitoUser => {
    const payload = JSON.parse(atob(idToken.split('.')[1]));

    return {
      id: payload.sub,
      email: payload.email,
      phone: payload.phone_number,
      firstName: payload.given_name,
      lastName: payload.family_name,
      tenantId: payload['custom:tenant_id'],
      role: payload['custom:role'],
      authMethod: payload['custom:auth_method'],
    };
  }, []);

  /**
   * Check if a token is expired
   * @param idToken - The JWT ID token to check
   * @returns true if token is expired, false otherwise
   */
  const checkTokenExpiry = useCallback((idToken: string | null): boolean => {
    if (!idToken) return true;

    try {
      const payload = JSON.parse(atob(idToken.split('.')[1]));
      const expiry = payload.exp * 1000; // Convert to milliseconds
      // Add 30 second buffer to account for clock skew
      return Date.now() >= expiry - 30000;
    } catch {
      return true;
    }
  }, []);

  // Load stored auth state on mount
  useEffect(() => {
    const loadStoredAuth = async () => {
      try {
        const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);

          // Check if tokens are expired
          const isExpired = checkTokenExpiry(parsed.idToken);
          if (!isExpired) {
            setState({
              ...parsed,
              isAuthenticated: true,
              isLoading: false,
              impersonateUserId: parsed.impersonateUserId || null,
            });
            return;
          }

          // Token expired but we have a refresh token - try to refresh
          if (parsed.refreshToken) {
            try {
              const { data } = await authRefreshTokenMutation({
                variables: { refreshToken: parsed.refreshToken },
              });

              if (data?.authRefreshToken) {
                // Parse user from new ID token
                const user = parseUserFromToken(data.authRefreshToken.idToken);

                setState({
                  user,
                  accessToken: data.authRefreshToken.accessToken,
                  idToken: data.authRefreshToken.idToken,
                  refreshToken: parsed.refreshToken, // Keep existing refresh token
                  isAuthenticated: true,
                  isLoading: false,
                  impersonateUserId: parsed.impersonateUserId || null,
                });

                return;
              }
            } catch (error) {
              // eslint-disable-next-line no-console
              console.log(
                '[Cognito Auth] Failed to refresh token on mount',
                error,
              );
              // Fall through to clear state below
            }
          }
        }
      } catch (_error) {
        // Stored token invalid or expired
      }

      // Clear any invalid/expired state
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      setState((prev) => ({ ...prev, isLoading: false }));
    };

    loadStoredAuth();
  }, [checkTokenExpiry, authRefreshTokenMutation, parseUserFromToken]);

  // Save auth state to localStorage whenever it changes
  useEffect(() => {
    if (state.isAuthenticated && state.accessToken) {
      localStorage.setItem(
        TOKEN_STORAGE_KEY,
        JSON.stringify({
          user: state.user,
          accessToken: state.accessToken,
          idToken: state.idToken,
          refreshToken: state.refreshToken,
          impersonateUserId: state.impersonateUserId,
        }),
      );
    }
  }, [state]);

  const checkUser = useCallback(
    async (
      identifier: string,
      redirectPath?: string,
    ): Promise<{
      exists: boolean;
      authMethod?: 'password' | 'otp' | 'google-sso';
      requiresPasswordReset?: boolean;
      redirectUrl?: string;
      otpSent?: boolean;
      session?: string;
      message?: string;
      resetCodeSent?: boolean;
      resetDestination?: string;
    }> => {
      const { data } = await authCheckUserMutation({
        variables: { identifier, redirectPath },
      });

      if (!data?.authCheckUser) {
        throw new Error('Failed to check user');
      }

      return {
        exists: data.authCheckUser.exists,
        authMethod: data.authCheckUser.authMethod as
          | 'password'
          | 'otp'
          | 'google-sso'
          | undefined,
        requiresPasswordReset:
          data.authCheckUser.requiresPasswordReset || false,
        redirectUrl: data.authCheckUser.redirectUrl || undefined,
        otpSent: data.authCheckUser.otpSent || false,
        session: data.authCheckUser.session || undefined,
        message: data.authCheckUser.message || undefined,
        resetCodeSent: data.authCheckUser.resetCodeSent || false,
        resetDestination: data.authCheckUser.resetDestination || undefined,
      };
    },
    [authCheckUserMutation],
  );

  const loginWithPassword = useCallback(
    async (identifier: string, password: string): Promise<void> => {
      const { data } = await authLoginPasswordMutation({
        variables: { identifier, password },
      });

      if (!data?.authLoginPassword) {
        throw new Error('Login failed');
      }

      if (data.authLoginPassword.requiresNewPassword) {
        throw new Error('Password reset required');
      }

      if (!data.authLoginPassword.idToken) {
        throw new Error('Login failed');
      }

      const user = parseUserFromToken(data.authLoginPassword.idToken);

      setState({
        user,
        accessToken: data.authLoginPassword.accessToken!,
        idToken: data.authLoginPassword.idToken,
        refreshToken: data.authLoginPassword.refreshToken!,
        isAuthenticated: true,
        isLoading: false,
        impersonateUserId: null,
      });
    },
    [authLoginPasswordMutation],
  );

  const sendOTP = useCallback(
    async (
      identifier: string,
    ): Promise<{ success: boolean; session?: string }> => {
      const { data } = await authSendOTPMutation({
        variables: { identifier },
      });

      if (!data?.authSendOTP?.success) {
        throw new Error('Failed to send OTP');
      }

      return {
        success: data.authSendOTP.success,
        session: data.authSendOTP.session || undefined,
      };
    },
    [authSendOTPMutation],
  );

  const loginWithOTP = useCallback(
    async (
      identifier: string,
      otp: string,
      session?: string,
    ): Promise<{ requiresProfileCompletion: boolean }> => {
      const { data } = await authVerifyOTPMutation({
        variables: { identifier, otp, session: session || null },
      });

      if (!data?.authVerifyOTP?.idToken) {
        throw new Error('OTP verification failed');
      }

      const user = parseUserFromToken(data.authVerifyOTP.idToken);

      setState({
        user,
        accessToken: data.authVerifyOTP.accessToken!,
        idToken: data.authVerifyOTP.idToken,
        refreshToken: data.authVerifyOTP.refreshToken!,
        isAuthenticated: true,
        isLoading: false,
        impersonateUserId: null,
      });

      return {
        requiresProfileCompletion:
          data.authVerifyOTP.requiresProfileCompletion || false,
      };
    },
    [authVerifyOTPMutation],
  );

  const loginWithOAuthCode = useCallback(
    async (code: string): Promise<void> => {
      // Determine redirect URI based on environment
      const redirectUri =
        window.location.origin === 'http://localhost:3000'
          ? 'http://localhost:3000/auth/callback'
          : `${window.location.origin}/auth/callback`;

      const { data } = await authLoginOAuthMutation({
        variables: { code, redirectUri },
      });

      if (!data?.authLoginOAuth?.idToken) {
        throw new Error('OAuth authentication failed');
      }

      const user = parseUserFromToken(data.authLoginOAuth.idToken);

      const newState = {
        user,
        accessToken: data.authLoginOAuth.accessToken,
        idToken: data.authLoginOAuth.idToken,
        refreshToken: data.authLoginOAuth.refreshToken,
        isAuthenticated: true,
        isLoading: false,
        impersonateUserId: null,
      };

      setState(newState);
    },
    [authLoginOAuthMutation],
  );

  const setNewPassword = useCallback(
    async (
      identifier: string,
      newPassword: string,
      temporaryPassword?: string,
    ): Promise<void> => {
      const { data } = await authSetPasswordMutation({
        variables: {
          identifier,
          newPassword,
          temporaryPassword,
          session: null,
        },
      });

      if (!data?.authSetPassword?.idToken) {
        throw new Error('Failed to set password');
      }

      const user = parseUserFromToken(data.authSetPassword.idToken);

      setState({
        user,
        accessToken: data.authSetPassword.accessToken!,
        idToken: data.authSetPassword.idToken,
        refreshToken: data.authSetPassword.refreshToken!,
        isAuthenticated: true,
        isLoading: false,
        impersonateUserId: null,
      });
    },
    [authSetPasswordMutation],
  );

  const forgotPassword = useCallback(
    async (
      identifier: string,
    ): Promise<{ success: boolean; destination?: string }> => {
      // Use gql template literal for raw GraphQL query
      const FORGOT_PASSWORD_MUTATION = gql`
        mutation AuthForgotPassword($identifier: String!) {
          authForgotPassword(identifier: $identifier) {
            success
            message
            destination
          }
        }
      `;

      const { data } = await unauthApolloClient.mutate({
        mutation: FORGOT_PASSWORD_MUTATION,
        variables: { identifier },
      });

      if (!data?.authForgotPassword?.success) {
        throw new Error('Failed to send reset code');
      }

      return {
        success: data.authForgotPassword.success,
        destination: data.authForgotPassword.destination,
      };
    },
    [],
  );

  const confirmForgotPassword = useCallback(
    async (
      identifier: string,
      code: string,
      newPassword: string,
    ): Promise<void> => {
      // Use gql template literal for raw GraphQL query
      const CONFIRM_FORGOT_PASSWORD_MUTATION = gql`
        mutation AuthConfirmForgotPassword(
          $identifier: String!
          $code: String!
          $newPassword: String!
        ) {
          authConfirmForgotPassword(
            identifier: $identifier
            code: $code
            newPassword: $newPassword
          ) {
            success
            message
            accessToken
            idToken
            refreshToken
            expiresIn
          }
        }
      `;

      const { data } = await unauthApolloClient.mutate({
        mutation: CONFIRM_FORGOT_PASSWORD_MUTATION,
        variables: { identifier, code, newPassword },
      });

      if (!data?.authConfirmForgotPassword?.success) {
        throw new Error('Failed to reset password');
      }

      // If tokens are returned, log the user in automatically
      if (data.authConfirmForgotPassword.idToken) {
        const user = parseUserFromToken(data.authConfirmForgotPassword.idToken);

        setState({
          user,
          accessToken: data.authConfirmForgotPassword.accessToken!,
          idToken: data.authConfirmForgotPassword.idToken,
          refreshToken: data.authConfirmForgotPassword.refreshToken!,
          isAuthenticated: true,
          isLoading: false,
          impersonateUserId: null,
        });
      }
    },
    [],
  );

  const logout = useCallback(
    async (options?: { preserveRedirect?: boolean }): Promise<void> => {
      localStorage.removeItem(TOKEN_STORAGE_KEY);

      setState({
        user: null,
        accessToken: null,
        idToken: null,
        refreshToken: null,
        isAuthenticated: false,
        isLoading: false,
        impersonateUserId: null,
      });

      // If preserveRedirect is true (e.g., session expired), preserve current location
      // Otherwise (e.g., manual logout), just go to login page
      if (options?.preserveRedirect) {
        const redirectPath = `${location.pathname}${location.search}`;
        navigate(`/login?redirect=${encodeURIComponent(redirectPath)}`);
      } else {
        navigate('/login');
      }
    },
    [navigate, location],
  );

  const refreshAccessToken = useCallback(async (): Promise<string> => {
    if (!state.refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const { data } = await authRefreshTokenMutation({
        variables: { refreshToken: state.refreshToken },
      });

      if (!data?.authRefreshToken) {
        throw new Error('Failed to refresh token');
      }

      setState((prev) => ({
        ...prev,
        accessToken: data.authRefreshToken!.accessToken,
        idToken: data.authRefreshToken!.idToken,
      }));

      return data.authRefreshToken.accessToken;
    } catch (_error) {
      // Refresh token expired or invalid - logout and preserve redirect
      await logout({ preserveRedirect: true });
      throw new Error('Session expired');
    }
  }, [state.refreshToken, authRefreshTokenMutation, logout]);

  const setImpersonateUserId = useCallback((userId: string | null): void => {
    setState((prev) => ({ ...prev, impersonateUserId: userId }));
  }, []);

  const clearImpersonation = useCallback((): void => {
    setState((prev) => ({ ...prev, impersonateUserId: null }));
  }, []);

  // Periodic token expiry check - runs every minute when authenticated
  useEffect(() => {
    if (!state.isAuthenticated || !state.idToken) {
      return;
    }

    const checkAndRefreshToken = async () => {
      const isExpired = checkTokenExpiry(state.idToken);

      if (isExpired) {
        try {
          // Try to refresh the token
          await refreshAccessToken();
        } catch (error) {
          // eslint-disable-next-line no-console
          console.log('[Cognito Auth] Token refresh failed, logging out');
          // refreshAccessToken already calls logout on failure
        }
      }
    };

    // Check immediately
    checkAndRefreshToken();

    // Check every 60 seconds
    const interval = setInterval(checkAndRefreshToken, 60000);

    return () => clearInterval(interval);
  }, [
    state.isAuthenticated,
    state.idToken,
    checkTokenExpiry,
    refreshAccessToken,
  ]);

  const value = useMemo<CognitoAuthContextValue>(
    () => ({
      ...state,
      checkUser,
      loginWithPassword,
      loginWithOTP,
      loginWithOAuthCode,
      sendOTP,
      forgotPassword,
      confirmForgotPassword,
      setNewPassword,
      logout,
      refreshAccessToken,
      checkTokenExpiry,
      setImpersonateUserId,
      clearImpersonation,
    }),
    [
      state,
      checkUser,
      loginWithPassword,
      loginWithOTP,
      loginWithOAuthCode,
      sendOTP,
      forgotPassword,
      confirmForgotPassword,
      setNewPassword,
      logout,
      refreshAccessToken,
      checkTokenExpiry,
      setImpersonateUserId,
      clearImpersonation,
    ],
  );

  return (
    <CognitoAuthContext.Provider value={value}>
      {children}
    </CognitoAuthContext.Provider>
  );
}

/**
 * Outer provider component that wraps with ApolloProvider
 * for unauthenticated GraphQL requests
 * @param props - Component props
 * @param props.children - Child components
 * @returns Provider component with Apollo and auth context
 */
export function CognitoAuthProvider({ children }: CognitoAuthProviderProps) {
  // In test mode (faker:dev / Playwright), bypass Cognito entirely.
  // Provide a mock context so every downstream consumer (ProtectedRoute,
  // AuthApolloProvider, useCognitoAuth) sees isAuthenticated: true.
  // isPlaywrightTestMode() is !PROD-guarded, so Vite dead-code-eliminates
  // this entire block from production builds.
  if (isPlaywrightTestMode()) {
    const noop = async () => {};
    const mockValue: CognitoAuthContextValue = {
      user: null,
      accessToken: 'mock_test_token',
      idToken: null,
      refreshToken: null,
      isAuthenticated: true,
      isLoading: false,
      impersonateUserId: null,
      checkUser: noop as any,
      loginWithPassword: noop as any,
      loginWithOTP: (async () => ({ requiresProfileCompletion: false })) as any,
      loginWithOAuthCode: noop as any,
      sendOTP: (async () => ({ success: true })) as any,
      forgotPassword: (async () => ({ success: true })) as any,
      confirmForgotPassword: noop as any,
      setNewPassword: noop as any,
      logout: noop as any,
      refreshAccessToken: (async () => 'mock_test_token') as any,
      checkTokenExpiry: () => false,
      setImpersonateUserId: () => {},
      clearImpersonation: () => {},
    };

    return (
      <ApolloProvider client={unauthApolloClient}>
        <CognitoAuthContext.Provider value={mockValue}>
          {children}
        </CognitoAuthContext.Provider>
      </ApolloProvider>
    );
  }

  return (
    <ApolloProvider client={unauthApolloClient}>
      <CognitoAuthProviderInner>{children}</CognitoAuthProviderInner>
    </ApolloProvider>
  );
}

/**
 * Hook to access Cognito auth context
 * @returns The Cognito authentication context
 */
export function useCognitoAuth(): CognitoAuthContextValue {
  const context = useContext(CognitoAuthContext);
  if (!context) {
    throw new Error('useCognitoAuth must be used within CognitoAuthProvider');
  }
  return context;
}
